import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { cn } from '../../shared/lib/cn';
import { clockTime } from '../../shared/lib/format';
import { useDebouncedValue } from '../../shared/lib/use-debounced-value';
import { Button } from '../../shared/ui/button';
import { DoneIcon } from '../../shared/ui/icons';
import { Input } from '../../shared/ui/input';
import { Note } from '../../shared/ui/note';
import { SectionPanel } from '../../shared/ui/section-panel';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { listBulkOperations } from '../../entities/bulk-operation';
import { listMembers } from '../../entities/organization';
import {
  OLDER_DATA,
  deletePreview,
  formatBytes,
  ownersText,
  useRetentionUsage,
  type TDeleteRequest,
  type TStorageCleanup,
} from '../../entities/retention';
import { DeleteConfirm } from './delete-confirm';
import { DeleteProgress } from './delete-progress';

type TScope = TDeleteRequest['scope'];

const OPTIONS: { scope: TScope; lead: string; rest: string }[] = [
  {
    scope: 'content',
    lead: 'Delete webhook bodies, responses and step results older than N days',
    rest: '— history stays',
  },
  { scope: 'all', lead: 'Delete everything older than N days', rest: '— content and history' },
];

const Option = ({
  selected,
  lead,
  rest,
  onSelect,
}: {
  selected: boolean;
  lead: string;
  rest: string;
  onSelect: () => void;
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    className={cn(
      'flex items-start gap-inset rounded-option bg-field-muted px-3 py-inset text-left text-data text-primary',
      selected && 'border border-border-focus',
    )}
  >
    <span
      aria-hidden
      className={cn(
        'mt-hair size-check shrink-0 rounded-full border-border-focus',
        selected ? 'border-4 border-accent' : 'border-2',
      )}
    />
    <span>
      {lead} <span className="text-muted">{rest}</span>
    </span>
  </button>
);

const Done = ({
  run,
  totalBytes,
  recountMinutes,
  timeZone,
}: {
  run: TStorageCleanup;
  totalBytes: number;
  recountMinutes: number;
  timeZone?: string;
}) => {
  const members = useQuery({ queryKey: ['members'], queryFn: listMembers });
  const who = members.data?.find((member) => member.userId === run.createdBy)?.userName;
  const finished = run.finishedAt ?? run.startedAt;
  const updates = new Date(Date.parse(finished) + (recountMinutes + 1) * 60_000).toISOString();
  return (
    <div className="rounded-panel border border-border-sunken p-4">
      <span className="inline-flex items-center gap-label text-data text-primary">
        <DoneIcon size="sm" className="text-success" />
        Deleted {formatBytes(run.removedBytes)} · {clockTime(finished, timeZone)}
      </span>
      <p className="mt-2 text-meta text-muted">
        {formatBytes(run.removedBytes)} from {ownersText(run.owners)}
        {who ? `, by ${who}` : ''}. Stored now ~
        {formatBytes(Math.max(0, totalBytes - run.removedBytes))} · updates by{' '}
        {clockTime(updates, timeZone)}.
      </p>
    </div>
  );
};

interface IDeleteNowProps {
  id?: string;
  organizationName: string;
  timeZone?: string;
}

/** Boards S1/S2, "Delete now": does not wait for tonight, cannot be undone. */
export const DeleteNow = ({ id, organizationName, timeZone }: IDeleteNowProps) => {
  const usage = useRetentionUsage();
  const [scope, setScope] = useState<TScope>('content');
  const [text, setText] = useState('30');
  const [confirming, setConfirming] = useState(false);
  const [started, setStarted] = useState<{ id: string; bytes: number | null } | null>(null);
  const days = Number(text);
  const valid = Number.isInteger(days) && days >= 1 && days <= 3650;
  const settled = useDebouncedValue(days, 300);

  const running = useQuery({
    queryKey: ['bulk-operations', 'purge', 'active'],
    queryFn: () => listBulkOperations('purge', true),
  });
  const preview = useQuery({
    queryKey: ['retention', 'delete-preview', scope, settled],
    queryFn: () => deletePreview({ scope, olderThanDays: settled }),
    enabled: valid && settled === days,
    placeholderData: keepPreviousData,
  });

  const active = started ?? (running.data?.[0] ? { id: running.data[0].id, bytes: null } : null);
  const blocked = usage.data ? !usage.data.settings.allowedByInstallation : false;
  const data = preview.data;
  const lastDelete = usage.data?.lastDelete;

  return (
    <SectionPanel
      id={id}
      title="Delete now"
      note="does not wait for tonight · cannot be undone"
      tone="danger"
    >
      <div className="grid grid-cols-3 gap-6 px-card py-4">
        <div
          className="col-span-2 flex flex-col gap-2"
          role="radiogroup"
          aria-label="What to delete"
        >
          {OPTIONS.map((option) => (
            <Option
              key={option.scope}
              selected={scope === option.scope}
              lead={option.lead}
              rest={option.rest}
              onSelect={() => setScope(option.scope)}
            />
          ))}
          <div className="mt-2 flex items-center gap-inset">
            <span className="text-meta text-primary">Older than</span>
            <Input
              size="row"
              mono
              aria-label="Older than, in days"
              inputMode="numeric"
              value={text}
              invalid={!valid}
              onChange={(event) => setText(event.target.value)}
              className="w-days-input"
            />
            <span className="text-meta text-muted">days</span>
          </div>
          {blocked && (
            <Note tone="danger">
              Removal is switched off for this whole installation (RETENTION_PRUNE_ALLOWED=false).
            </Note>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {active ? (
            <DeleteProgress
              operationId={active.id}
              bytes={active.bytes}
              onFinished={() => {
                setStarted(null);
                void running.refetch();
              }}
            />
          ) : (
            <div className="rounded-card bg-field-muted px-input py-3">
              <p className="font-mono text-micro text-faint">Would delete</p>
              {preview.isError && <ErrorState error={preview.error} />}
              {!data && preview.isFetching && <LoadingState rows={2} />}
              {data && (
                <>
                  <p className="mt-label text-figure font-semibold text-primary">
                    ~{formatBytes(data.bytes)}{' '}
                    {data.owners.length > 0 && (
                      <span className="text-data font-normal text-muted">
                        from {ownersText(data.owners)}
                      </span>
                    )}
                  </p>
                  <p className="mt-label text-meta leading-18 text-muted">
                    {data.olderBytes > 0 &&
                      `${OLDER_DATA}: ${formatBytes(data.olderBytes)} of it. `}
                    {data.requests.toLocaleString('en-US')} requests. Not touched:{' '}
                    {data.keptUnfinished.toLocaleString('en-US')}{' '}
                    {data.keptUnfinished === 1 ? 'copy' : 'copies'} still being delivered and{' '}
                    {data.keptBookmarked.toLocaleString('en-US')} bookmarked.
                  </p>
                  {scope === 'all' && data.runs > 0 && (
                    <Note tone="caution" className="mt-2">
                      This includes history. {data.runs.toLocaleString('en-US')} runs older than{' '}
                      {data.olderThanDays} days disappear from Events and their traces.
                    </Note>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    destructive
                    className="mt-3"
                    disabled={blocked || data.bytes === 0 || !valid}
                    onClick={() => setConfirming(true)}
                  >
                    {data.bytes === 0 ? 'Nothing that old' : `Delete ~${formatBytes(data.bytes)}…`}
                  </Button>
                </>
              )}
            </div>
          )}
          {!active && lastDelete?.finishedAt && usage.data && (
            <Done
              run={lastDelete}
              totalBytes={usage.data.totalBytes}
              recountMinutes={usage.data.settings.recountMinutes}
              timeZone={timeZone}
            />
          )}
        </div>
      </div>
      {confirming && data && (
        <DeleteConfirm
          preview={data}
          organizationName={organizationName}
          onClose={() => setConfirming(false)}
          onStarted={(id) => {
            setConfirming(false);
            setStarted({ id, bytes: data.bytes });
          }}
        />
      )}
    </SectionPanel>
  );
};
